import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { PRODUCT_KEY_BY_PARAM, PARTTIME_LINK_LABELS } from '../Form';
import {
  Typography, Grid, TextField, FormControlLabel, Checkbox,
  Button, MenuItem, Divider, Box, Alert,
} from '@mui/material';
import ImageUploadField from './ImageUploadField';

// Fixed sets because the app code only knows how to render these keys - see
// STEP_DEFINITIONS in src/Form.js. Adding a new key here without a matching
// code change would silently do nothing.
const STEP_OPTIONS = [
  { key: 'information', label: 'Personal Information' },
  { key: 'emergency', label: 'Emergency Contact' },
  { key: 'waiver', label: 'Release of Liability (your own waiver text)' },
  { key: 'campWaiver', label: "Camp Waiver (venue's own waiver, e.g. SmartWaiver)" },
  { key: 'services', label: 'Services' },
  { key: 'merch', label: 'Merchandise' },
  { key: 'review', label: 'Review' },
  { key: 'payment', label: 'Payment' },
];

// Full-time pricing applies automatically based on the registrant's status -
// no link, no toggle.
const FULL_TIME_TYPES = [
  { key: 'FT-STUDENT', label: 'Full Time - Student' },
  { key: 'FT-NONSTUDENT', label: 'Full Time - Non-Student' },
];

// Special-link options: only reachable via ?parttime=<param>, and can be
// turned off (falls back to the normal full-time flow) without losing the
// price if it's needed again later. Derived from src/Form.js's
// PRODUCT_KEY_BY_PARAM so the admin page and the app never drift apart.
const SPECIAL_LINK_TYPES = Object.entries(PRODUCT_KEY_BY_PARAM).map(([param, key]) => ({
  key,
  param,
  label: PARTTIME_LINK_LABELS[key] || key,
}));

const PRODUCT_TYPES = [...FULL_TIME_TYPES, ...SPECIAL_LINK_TYPES];

// Canonical step ordering, used to keep enabledSteps sorted correctly no
// matter what order the admin toggles checkboxes in (see toggleStep below).
const STEP_ORDER = STEP_OPTIONS.map(({ key }) => key);

const BLANK_CONFERENCE = {
  name: '',
  regularDeadline: '',
  finalDeadline: '',
  allowLateRegistration: false,
  noLateFee: false,
  bannerImageUrl: '',
  headerMarkdown: '',
  waiverMarkdown: '',
  campWaiverUrl: '',
  campWaiverName: '',
  emailTemplateMarkdown: '',
  // Release of Liability is on by default since it applies to every
  // conference; Camp Waiver is venue-dependent so it's off until a URL is set.
  enabledSteps: ['information', 'emergency', 'waiver', 'review', 'payment'],
  // All special links on by default, matching behavior before this toggle existed.
  enabledPartTimeOptions: SPECIAL_LINK_TYPES.map(({ key }) => key),
  products: Object.fromEntries(PRODUCT_TYPES.map(({ key }) => [
    key,
    { name: 'Registration Cost', description: '', quantity: 1, unit_amount: { value: 0, currency_code: 'USD' } },
  ])),
  lateFeeProduct: { name: 'Late Fee', description: 'Late Registration Fee', quantity: 1, unit_amount: { value: 0, currency_code: 'USD' } },
  // Shown on the Merchandise step (see STEP_OPTIONS 'merch' above) - that
  // checkbox is the on/off switch, so there's no separate "enabled" flag here.
  tshirt: {
    name: 'T-Shirt',
    description: '',
    imageUrl: '',
    unit_amount: { value: 17, currency_code: 'USD' },
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
  },
  // Special links that pre-fill the "I have financial support" section for
  // whoever opens them (e.g. a partner church that always covers the same
  // amount) - see the "Financial support pre-fill links" section below.
  // Not something a registrant types in; for that, see checkoutDiscountCodes.
  discountCodes: {},
  // Codes a registrant types in themselves on the Review step to reduce
  // their total and/or waive the late fee - see "Checkout discount codes"
  // below. Distinct from discountCodes (financial support pre-fill links).
  checkoutDiscountCodes: {},
  // Shown as checkboxes on the Services step (see STEP_OPTIONS 'services'
  // above) - just plain labels, no fixed keys, so adding/removing/renaming
  // one here needs no code change.
  serviceOptions: [],
};

function discountMapToRows(discountCodes) {
  return Object.entries(discountCodes || {}).map(([code, v]) => ({ code, ...v }));
}

function discountRowsToMap(rows) {
  const map = {};
  for (const row of rows) {
    if (!row.code) continue;
    const { code, ...rest } = row;
    map[code] = rest;
  }
  return map;
}

function checkoutDiscountMapToRows(checkoutDiscountCodes) {
  return Object.entries(checkoutDiscountCodes || {}).map(([code, v]) => ({ code, ...v }));
}

function checkoutDiscountRowsToMap(rows) {
  const map = {};
  for (const row of rows) {
    if (!row.code) continue;
    const { code, ...rest } = row;
    map[code] = rest;
  }
  return map;
}

// Lets staff stand up/edit a conference (deadlines, prices, discount codes,
// which steps show, banner/markdown content) and flip settings/active to
// launch it - without touching code or redeploying. See
// conferences/{conferenceId} and settings/active in Firestore.
export default function ConferenceSettingsPanel({ canEdit, canActivate }) {
  const [conferenceIds, setConferenceIds] = useState([]);
  const [activeConferenceId, setActiveConferenceId] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [newId, setNewId] = useState('');
  const [form, setForm] = useState(BLANK_CONFERENCE);
  const [discountRows, setDiscountRows] = useState([]);
  const [checkoutDiscountRows, setCheckoutDiscountRows] = useState([]);
  const [tshirtSizesInput, setTshirtSizesInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [activeSnap, confsSnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'active')),
          getDocs(collection(db, 'conferences')),
        ]);
        const ids = confsSnap.docs.map((d) => d.id);
        const active = activeSnap.data()?.conferenceId || '';
        setConferenceIds(ids);
        setActiveConferenceId(active);
        setSelectedId(active || ids[0] || '');
      } catch (error) {
        console.error('Failed to load conference list:', error);
        setMessage({ type: 'error', text: error.message });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    (async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'conferences', selectedId));
        const data = snap.exists() ? { ...BLANK_CONFERENCE, ...snap.data() } : BLANK_CONFERENCE;
        setForm(data);
        setDiscountRows(discountMapToRows(data.discountCodes));
        setCheckoutDiscountRows(checkoutDiscountMapToRows(data.checkoutDiscountCodes));
        setTshirtSizesInput((data.tshirt?.sizes || []).join(', '));
      } catch (error) {
        console.error('Failed to load conference doc:', error);
        setMessage({ type: 'error', text: error.message });
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedId]);

  function updateField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function updateProduct(key, field, value) {
    setForm((f) => ({
      ...f,
      products: {
        ...f.products,
        [key]: {
          ...f.products[key],
          ...(field === 'price'
            ? { unit_amount: { ...f.products[key]?.unit_amount, value: Number(value) } }
            : { [field]: value }),
        },
      },
    }));
  }

  function updateLateFee(field, value) {
    setForm((f) => ({
      ...f,
      lateFeeProduct: {
        ...f.lateFeeProduct,
        ...(field === 'price'
          ? { unit_amount: { ...f.lateFeeProduct?.unit_amount, value: Number(value) } }
          : { [field]: value }),
      },
    }));
  }

  function updateTshirt(field, value) {
    setForm((f) => ({
      ...f,
      tshirt: {
        ...f.tshirt,
        ...(field === 'price'
          ? { unit_amount: { ...f.tshirt?.unit_amount, value: Number(value) } }
          : { [field]: value }),
      },
    }));
  }

  function toggleStep(key) {
    setForm((f) => {
      const has = f.enabledSteps.includes(key);
      const next = has ? f.enabledSteps.filter((k) => k !== key) : [...f.enabledSteps, key];
      // Keep steps in the canonical STEP_OPTIONS order regardless of toggle
      // order, so e.g. enabling Services always lands before Review/Payment.
      next.sort((a, b) => STEP_ORDER.indexOf(a) - STEP_ORDER.indexOf(b));
      return { ...f, enabledSteps: next };
    });
  }

  function toggleSpecialLink(key) {
    setForm((f) => {
      const enabled = f.enabledPartTimeOptions || [];
      const has = enabled.includes(key);
      return { ...f, enabledPartTimeOptions: has ? enabled.filter((k) => k !== key) : [...enabled, key] };
    });
  }

  function updateServiceOption(index, value) {
    setForm((f) => ({
      ...f,
      serviceOptions: f.serviceOptions.map((opt, i) => (i === index ? value : opt)),
    }));
  }

  function addServiceOption() {
    setForm((f) => ({ ...f, serviceOptions: [...(f.serviceOptions || []), ''] }));
  }

  function removeServiceOption(index) {
    setForm((f) => ({ ...f, serviceOptions: f.serviceOptions.filter((_, i) => i !== index) }));
  }

  function copySpecialLink(param) {
    const url = `${window.location.origin}/?parttime=${param}`;
    navigator.clipboard.writeText(url)
      .then(() => setMessage({ type: 'success', text: `Copied: ${url}` }))
      .catch((error) => setMessage({ type: 'error', text: `Could not copy link: ${error.message}` }));
  }

  // Bypasses the "Registration is now closed" page after the final deadline
  // - see the isClosed check in App.js, which only honors this link when
  // form.allowLateRegistration (toggled below) is also checked, so the link
  // can be turned off without losing it.
  function copyLateRegistrationLink() {
    const url = `${window.location.origin}/?allowlateregistration=true`;
    navigator.clipboard.writeText(url)
      .then(() => setMessage({ type: 'success', text: `Copied: ${url}` }))
      .catch((error) => setMessage({ type: 'error', text: `Could not copy link: ${error.message}` }));
  }

  function copyDiscountLink(code) {
    const trimmed = (code || '').trim();
    if (!trimmed) return;
    const url = `${window.location.origin}/?discount=${encodeURIComponent(trimmed)}`;
    navigator.clipboard.writeText(url)
      .then(() => setMessage({ type: 'success', text: `Copied: ${url}` }))
      .catch((error) => setMessage({ type: 'error', text: `Could not copy link: ${error.message}` }));
  }

  function updateDiscountRow(index, field, value) {
    setDiscountRows((rows) => rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  }

  function addDiscountRow() {
    setDiscountRows((rows) => [...rows, { code: '', financialSupportAmount: 0, financialSupportSource: '', servingOneName: '', servingOneEmail: '' }]);
  }

  function removeDiscountRow(index) {
    setDiscountRows((rows) => rows.filter((_, i) => i !== index));
  }

  function updateCheckoutDiscountRow(index, field, value) {
    setCheckoutDiscountRows((rows) => rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  }

  function addCheckoutDiscountRow() {
    setCheckoutDiscountRows((rows) => [...rows, { code: '', amountOff: 0, waiveLateFee: false, description: '' }]);
  }

  function removeCheckoutDiscountRow(index) {
    setCheckoutDiscountRows((rows) => rows.filter((_, i) => i !== index));
  }

  async function handleCreate() {
    const id = newId.trim();
    if (!id) return;
    try {
      await setDoc(doc(db, 'conferences', id), BLANK_CONFERENCE);
      setConferenceIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
      setNewId('');
      setSelectedId(id);
      setMessage({ type: 'success', text: `Created conferences/${id}.` });
    } catch (error) {
      console.error('Failed to create conference:', error);
      setMessage({ type: 'error', text: error.message });
    }
  }

  async function handleSave() {
    setMessage(null);
    try {
      const payload = {
        ...form,
        discountCodes: discountRowsToMap(discountRows),
        checkoutDiscountCodes: checkoutDiscountRowsToMap(checkoutDiscountRows),
        serviceOptions: (form.serviceOptions || []).map((s) => s.trim()).filter(Boolean),
        tshirt: {
          ...form.tshirt,
          sizes: tshirtSizesInput.split(',').map((s) => s.trim()).filter(Boolean),
        },
      };
      await setDoc(doc(db, 'conferences', selectedId), payload);
      setMessage({ type: 'success', text: `Saved conferences/${selectedId}.` });
    } catch (error) {
      console.error('Failed to save conference:', error);
      setMessage({ type: 'error', text: error.message });
    }
  }

  async function handleMakeActive() {
    setMessage(null);
    try {
      await setDoc(doc(db, 'settings', 'active'), { conferenceId: selectedId });
      setActiveConferenceId(selectedId);
      setMessage({ type: 'success', text: `The live site now shows "${selectedId}".` });
    } catch (error) {
      console.error('Failed to set active conference:', error);
      setMessage({ type: 'error', text: error.message });
    }
  }

  return (
    <Box sx={{ maxWidth: 900 }}>
      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have view-only access. Ask an admin to grant EDITOR or ADMIN access to save changes here.
        </Alert>
      )}
      {canEdit && !canActivate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can edit and save conference settings, but only an ADMIN can switch which conference is live on the site.
        </Alert>
      )}
      {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={5}>
          <TextField
            select fullWidth label="Editing conference"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            {conferenceIds.map((id) => (
              <MenuItem key={id} value={id}>{id}{id === activeConferenceId ? ' (active)' : ''}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth label="New conference ID" placeholder="e.g. fa26"
            value={newId} onChange={(e) => setNewId(e.target.value)} disabled={!canEdit}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Button fullWidth variant="outlined" onClick={handleCreate} disabled={!canEdit || !newId.trim()}>
            Create
          </Button>
        </Grid>
      </Grid>

      <Typography variant="body2" sx={{ mb: 2 }}>
        Currently live on the site: <strong>{activeConferenceId || '(none set)'}</strong>
      </Typography>

      <Divider sx={{ mb: 2 }} />

      {loading ? <Typography>Loading...</Typography> : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Conference name" value={form.name}
                onChange={(e) => updateField('name', e.target.value)} disabled={!canEdit} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Regular deadline" helperText="Format: 3/2/26 12:00 AM"
                value={form.regularDeadline} onChange={(e) => updateField('regularDeadline', e.target.value)} disabled={!canEdit} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Final deadline" helperText="Format: 3/5/26 9:00 AM"
                value={form.finalDeadline} onChange={(e) => updateField('finalDeadline', e.target.value)} disabled={!canEdit} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox checked={!!form.noLateFee}
                  onChange={(e) => updateField('noLateFee', e.target.checked)} disabled={!canEdit} />}
                label="Never charge a late fee" />
            </Grid>
            <Grid item xs={12}>
              <ImageUploadField label="Banner image"
                helperText="Upload an image, or paste a public URL directly. Leave blank to show no banner."
                value={form.bannerImageUrl} onChange={(url) => updateField('bannerImageUrl', url)} disabled={!canEdit}
                storagePath={`${selectedId}/banner`} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} label="Header text (shown under the banner, Markdown supported)"
                value={form.headerMarkdown} onChange={(e) => updateField('headerMarkdown', e.target.value)} disabled={!canEdit} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={4} label="Release of Liability text (shown on the Release of Liability step, Markdown supported)"
                value={form.waiverMarkdown} onChange={(e) => updateField('waiverMarkdown', e.target.value)} disabled={!canEdit} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Camp waiver URL"
                helperText="e.g. a SmartWaiver embed link. Leave blank and disable the Camp Waiver step if this conference's venue doesn't need one."
                value={form.campWaiverUrl} onChange={(e) => updateField('campWaiverUrl', e.target.value)} disabled={!canEdit} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Camp/venue name"
                helperText="e.g. Mission Springs"
                value={form.campWaiverName} onChange={(e) => updateField('campWaiverName', e.target.value)} disabled={!canEdit} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} label="Confirmation email text"
                helperText="Use {{name}} where the registrant's name should be inserted"
                value={form.emailTemplateMarkdown} onChange={(e) => updateField('emailTemplateMarkdown', e.target.value)} disabled={!canEdit} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Registration steps</Typography>
          <Grid container spacing={1}>
            {STEP_OPTIONS.map(({ key, label }) => (
              <Grid item xs={6} sm={4} key={key}>
                <FormControlLabel
                  control={<Checkbox checked={form.enabledSteps.includes(key)} onChange={() => toggleStep(key)} disabled={!canEdit} />}
                  label={label}
                />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Full time pricing</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Applies automatically based on what the registrant selects as their status on the Personal Information
            step - student statuses get Student pricing, everyone else gets Non-Student pricing. No link needed.
          </Typography>
          <Grid container spacing={2}>
            {FULL_TIME_TYPES.map(({ key, label }) => (
              <React.Fragment key={key}>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ mt: 2 }}>{label}</Typography>
                </Grid>
                <Grid item xs={8} sm={5}>
                  <TextField fullWidth label="Description" value={form.products[key]?.description || ''}
                    onChange={(e) => updateProduct(key, 'description', e.target.value)} disabled={!canEdit} />
                </Grid>
                <Grid item xs={4} sm={3}>
                  <TextField fullWidth type="number" label="Price (USD)" value={form.products[key]?.unit_amount?.value ?? 0}
                    onChange={(e) => updateProduct(key, 'price', e.target.value)} disabled={!canEdit} />
                </Grid>
              </React.Fragment>
            ))}
            <Grid item xs={12} sm={4}>
              <Typography sx={{ mt: 2 }}>Late fee</Typography>
            </Grid>
            <Grid item xs={8} sm={5}>
              <TextField fullWidth label="Description" value={form.lateFeeProduct?.description || ''}
                onChange={(e) => updateLateFee('description', e.target.value)} disabled={!canEdit} />
            </Grid>
            <Grid item xs={4} sm={3}>
              <TextField fullWidth type="number" label="Price (USD)" value={form.lateFeeProduct?.unit_amount?.value ?? 0}
                onChange={(e) => updateLateFee('price', e.target.value)} disabled={!canEdit} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Special link options</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each only appears to someone who opens that specific link - a typical registrant never sees these as a
            choice. Turning one off makes its link fall back to normal full-time pricing instead of erroring, without
            losing the price/description if you turn it back on later.
          </Typography>
          {SPECIAL_LINK_TYPES.map(({ key, label, param }) => (
            <Grid container spacing={2} key={key} alignItems="center" sx={{ mb: 1 }}>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={<Checkbox checked={(form.enabledPartTimeOptions || []).includes(key)}
                    onChange={() => toggleSpecialLink(key)} disabled={!canEdit} />}
                  label={label}
                />
              </Grid>
              <Grid item xs={7} sm={4}>
                <TextField fullWidth label="Description" value={form.products[key]?.description || ''}
                  onChange={(e) => updateProduct(key, 'description', e.target.value)} disabled={!canEdit} />
              </Grid>
              <Grid item xs={5} sm={2}>
                <TextField fullWidth type="number" label="Price (USD)" value={form.products[key]?.unit_amount?.value ?? 0}
                  onChange={(e) => updateProduct(key, 'price', e.target.value)} disabled={!canEdit} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button fullWidth variant="outlined" onClick={() => copySpecialLink(param)}>
                  Copy link
                </Button>
              </Grid>
            </Grid>
          ))}

          <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={<Checkbox checked={!!form.allowLateRegistration}
                  onChange={(e) => updateField('allowLateRegistration', e.target.checked)} disabled={!canEdit} />}
                label="Late Registration"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Bypasses "Registration is now closed" after the final deadline - normal pricing and late fee still
                apply. Turn off to disable the link without losing this setting.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button fullWidth variant="outlined" onClick={copyLateRegistrationLink}>
                Copy link
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>T-shirt pre-sale</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Only shown if "Merchandise" is checked under Registration steps above. Selecting "None" for size skips
            the purchase.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Item name" value={form.tshirt?.name || ''}
                onChange={(e) => updateTshirt('name', e.target.value)} disabled={!canEdit} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Price (USD)" value={form.tshirt?.unit_amount?.value ?? 0}
                onChange={(e) => updateTshirt('price', e.target.value)} disabled={!canEdit} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Description (shown above the size picker)"
                helperText='Leave blank to use a default like "T-shirts cost $17. If you would like one, please select a size below."'
                value={form.tshirt?.description || ''} onChange={(e) => updateTshirt('description', e.target.value)} disabled={!canEdit} />
            </Grid>
            <Grid item xs={12}>
              <ImageUploadField label="T-shirt image"
                helperText="Upload an image, or paste a public URL directly. Leave blank to show no image."
                value={form.tshirt?.imageUrl || ''} onChange={(url) => updateTshirt('imageUrl', url)} disabled={!canEdit}
                storagePath={`${selectedId}/tshirt`} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Available sizes"
                helperText="Comma-separated, e.g. S, M, L, XL, XXL"
                value={tshirtSizesInput} onChange={(e) => setTshirtSizesInput(e.target.value)} disabled={!canEdit} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Checkout discount codes</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Registrants type one of these codes in themselves on the Review step to reduce their total and/or waive
            the late fee. Matched case-insensitively. Different from the financial support pre-fill links below,
            which a registrant never types - those only work via a special link.
          </Typography>
          {checkoutDiscountRows.map((row, i) => (
            <Grid container spacing={1} key={i} sx={{ mb: 1 }} alignItems="center">
              <Grid item xs={6} sm={2}>
                <TextField fullWidth label="Code" value={row.code}
                  onChange={(e) => updateCheckoutDiscountRow(i, 'code', e.target.value)} disabled={!canEdit} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth type="number" label="Amount off ($)" value={row.amountOff ?? 0}
                  onChange={(e) => updateCheckoutDiscountRow(i, 'amountOff', Number(e.target.value))} disabled={!canEdit} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Description (shown to registrant)" value={row.description || ''}
                  onChange={(e) => updateCheckoutDiscountRow(i, 'description', e.target.value)} disabled={!canEdit} />
              </Grid>
              <Grid item xs={8} sm={3}>
                <FormControlLabel
                  control={<Checkbox checked={!!row.waiveLateFee}
                    onChange={(e) => updateCheckoutDiscountRow(i, 'waiveLateFee', e.target.checked)} disabled={!canEdit} />}
                  label="Waives late fee" />
              </Grid>
              <Grid item xs={4} sm={2}>
                <Button color="error" onClick={() => removeCheckoutDiscountRow(i)} disabled={!canEdit}>Remove</Button>
              </Grid>
            </Grid>
          ))}
          <Button onClick={addCheckoutDiscountRow} disabled={!canEdit}>+ Add checkout discount code</Button>

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Financial support pre-fill links</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each row is a special link (like the special link options above) that pre-fills the "I have financial
            support" section for whoever opens it - useful for a partner club/church that always covers the same
            amount. A registrant never types these in; for a code they type themselves, see "Checkout discount
            codes" above.
          </Typography>
          {discountRows.map((row, i) => (
            <Grid container spacing={1} key={i} sx={{ mb: 1 }} alignItems="center">
              <Grid item xs={6} sm={2}>
                <TextField fullWidth label="Code" value={row.code}
                  onChange={(e) => updateDiscountRow(i, 'code', e.target.value)} disabled={!canEdit} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth type="number" label="Amount off ($)" value={row.financialSupportAmount ?? 0}
                  onChange={(e) => updateDiscountRow(i, 'financialSupportAmount', Number(e.target.value))} disabled={!canEdit} />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField fullWidth label="Source (club/church)" value={row.financialSupportSource || ''}
                  onChange={(e) => updateDiscountRow(i, 'financialSupportSource', e.target.value)} disabled={!canEdit} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth label="Serving One name" value={row.servingOneName || ''}
                  onChange={(e) => updateDiscountRow(i, 'servingOneName', e.target.value)} disabled={!canEdit} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth label="Serving One email" value={row.servingOneEmail || ''}
                  onChange={(e) => updateDiscountRow(i, 'servingOneEmail', e.target.value)} disabled={!canEdit} />
              </Grid>
              <Grid item xs={6} sm={1}>
                <Button fullWidth variant="outlined" size="small" onClick={() => copyDiscountLink(row.code)} disabled={!row.code}>
                  Copy link
                </Button>
              </Grid>
              <Grid item xs={6} sm={1}>
                <Button color="error" onClick={() => removeDiscountRow(i)} disabled={!canEdit}>Remove</Button>
              </Grid>
            </Grid>
          ))}
          <Button onClick={addDiscountRow} disabled={!canEdit}>+ Add financial support link</Button>

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Service options</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Shown as checkboxes on the Services step (only appears if "Services" is checked under Registration
            steps above). Add, remove, or rename options here - no code change needed.
          </Typography>
          {(form.serviceOptions || []).map((option, i) => (
            <Grid container spacing={1} key={i} sx={{ mb: 1 }} alignItems="center">
              <Grid item xs={9} sm={10}>
                <TextField fullWidth label={`Option ${i + 1}`} value={option}
                  onChange={(e) => updateServiceOption(i, e.target.value)} disabled={!canEdit} />
              </Grid>
              <Grid item xs={3} sm={2}>
                <Button color="error" onClick={() => removeServiceOption(i)} disabled={!canEdit}>Remove</Button>
              </Grid>
            </Grid>
          ))}
          <Button onClick={addServiceOption} disabled={!canEdit}>+ Add service option</Button>

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleSave} disabled={!canEdit || !selectedId}>
              Save
            </Button>
            <Button variant="outlined" onClick={handleMakeActive} disabled={!canActivate || !selectedId || selectedId === activeConferenceId}>
              Make this the active conference
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
